import { useState, useEffect } from "react";
import { PublicKey } from "@solana/web3.js";
import axios from "axios";
import { getAssociatedTokenAddress, getAccount } from "@solana/spl-token";
import { useSelector } from "react-redux";
import { connection } from "../../constants/constants";
import useContractInit from "./contractInit";

function useGetLALBalance() {
  const { saleAuthorityAddress, getTokenInitializationDetails } =
    useContractInit();
  const [treasuryBalance, setTreasuryBalance] = useState(0);
  const [devBalance, setDevBalance] = useState(0);
  const [treasuryWalletAddress, setTreasuryWalletAddress] = useState("");
  const [devWalletAddress, setDevWalletAddress] = useState("");
  const [contractBalance, setContractBalance] = useState(0);

  const user = useSelector((state) => state.userStore.user);
  const token = user.auth;

  useEffect(() => {
    const getBalances = async () => {
      await updateBalanceLAL();
    };
    if (treasuryWalletAddress && devWalletAddress && saleAuthorityAddress) {
      getBalances();
    }
  }, [treasuryWalletAddress, devWalletAddress, saleAuthorityAddress]);

  const fetchLFCPair = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/deployment/getLatestDeployedTokenAdmin`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const latestDeployedTokenAddress =
        response.data.data?.tokenContractAddress || "";
      return new PublicKey(latestDeployedTokenAddress);
    } catch (error) {
      console.error("Failed to fetch LFC pair:", error);
    }
  };

  const fetchLatestDeployedAndMintedToken = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/deployment/getLatestDeployedTokenAdmin`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const treasuryWalletAddressAPI = response.data.data?.treasuryWallet || "";
      setTreasuryWalletAddress(treasuryWalletAddressAPI);
      const devWalletAddressAPI = response.data.data?.devWallet || "";
      setDevWalletAddress(devWalletAddressAPI);
    } catch (error) {
      console.error("error", error);
    }
  };

  useEffect(() => {
    fetchLatestDeployedAndMintedToken();
    getTokenInitializationDetails();
  }, []);

  const getTokenAddressAndInstruction = async (mint, owner) => {
    const mintPublicKey = new PublicKey(mint);
    const tokenAccountAddress = await getAssociatedTokenAddress(
      mintPublicKey,
      owner,
      true
    );
    const accountInfo = await connection.getAccountInfo(tokenAccountAddress);
    if (accountInfo !== null) {
      return tokenAccountAddress;
    }
    return null;
  };

  const getUserLFCBalance = async (connection, userLfcTokenAccount) => {
    try {
      const userAccountInfo = await getAccount(
        connection,
        userLfcTokenAccount,
        "confirmed"
      );
      return userAccountInfo.amount;
    } catch (error) {
      console.error("Error getting LFC balance:", error);
      return 0;
    }
  };

  const updateBalanceLAL = async () => {
    const LFC_PAIR = await fetchLFCPair();

    const fetchAndSetBalance = async (walletAddress, setBalance) => {
      const userLfcTokenAccount = await getTokenAddressAndInstruction(
        LFC_PAIR,
        new PublicKey(walletAddress)
      );
      if (userLfcTokenAccount) {
        const userLfcBalance = await getUserLFCBalance(
          connection,
          userLfcTokenAccount
        );
        const updatedBalance = (Number(userLfcBalance) / 1000000).toFixed(5);
        setBalance(updatedBalance);
      } else {
        setBalance(0);
      }
    };

    if (treasuryWalletAddress) {
      await fetchAndSetBalance(treasuryWalletAddress, setTreasuryBalance);
    }

    if (devWalletAddress) {
      await fetchAndSetBalance(devWalletAddress, setDevBalance);
    }
    if (saleAuthorityAddress) {
      await fetchAndSetBalance(saleAuthorityAddress, setContractBalance);
    }
  };

  return [treasuryBalance, devBalance, contractBalance];
}

export default useGetLALBalance;
